import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Alert, Skeleton } from "@offstreetparking/design-system/feedback";
import { formatDuration } from "@offstreetparking/design-system/formatters";
import { Button, FormCheckbox, FormPhoneInput, useEnhancedForm } from "@offstreetparking/design-system/forms";
import { AddIcon } from "@offstreetparking/design-system/icons/AddIcon";
import { InfoIcon } from "@offstreetparking/design-system/icons/InfoIcon";
import { VehicleIcon } from "@offstreetparking/design-system/icons/VehicleIcon";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { zodPlateValidation } from "@offstreetparking/domain/entities/vehicle";
import { RFC_5322_EMAIL_REGEX } from "@offstreetparking/domain/value/email";
import type { CountryValue } from "@offstreetparking/geography";
import { useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { FormProvider, useFieldArray } from "react-hook-form";
import { offstreetClient } from "../../../../api/client";
import { ParkingCost, PaymentScheduleOverview, PriceBreakdown } from "../../../dataDisplay/parkingDetails";
import { ParkingDetailsRow } from "../../../dataDisplay/parkingDetails/ParkingDetailsRow";
import { EmailInput, EmailValidationModal, suggestEmail } from "../../../forms/EmailInput";
import { WysiwygView } from "../../../forms/Wysiwyg/WysiwygView";
import { useKiosk } from "../../../providers/KioskProvider";
import { useZod } from "../../../providers/TranslationsProvider";
import { CreateVehicleModal } from "../../../vehicles/CreateVehicleModal";
import { usePermitFormActions, usePermitFormData } from "../../PermitContextProvider";
import { usePermitWizardSettings } from "../../PermitContextProvider/PermitContextProvider";
import type { ParkingDetailsFormData } from "../../PermitContextProvider/types";
import { resolveRegistrationInstructions } from "../../resolveRegistrationInstructions";
import type { PermitTypeDto, PreviewInvoice } from "../../types";
import { SelectVehiclesModal } from "./SelectVehiclesModal";
import { SelectedVehiclesSummary } from "./SelectedVehiclesSummary";
import { VehicleInput } from "./VehicleInput";
import { getDefaultVehicleState } from "./get-default-vehicle-state";
import { clearRememberedInfo, getRememberedInfo, setRememberedInfo } from "./remembered-info";
import { useRememberedInfo } from "./useRememberedInfo";
let _isValidPhoneNumber: ((value: string) => boolean) | undefined;
import("libphonenumber-js").then((mod) => {
	_isValidPhoneNumber = mod.isValidPhoneNumber;
});
type ParkingDetailsFormProps = {
	formId: string;
	permitType: PermitTypeDto;
	invoice?: PreviewInvoice;
	initialValues?: Partial<ParkingDetailsFormData>;
	currency: string;
	onSubmit: (data: ParkingDetailsFormData) => void;
	isEmailRequired?: boolean;
	isLoggedIn: boolean;
	isSmsEnabled: boolean;
	isSmsRequired?: boolean;
	registrationInstructions?: string;
};
export const ParkingDetailsForm = ({
	formId,
	permitType,
	initialValues,
	currency,
	invoice,
	onSubmit,
	isEmailRequired,
	isLoggedIn,
	isSmsEnabled,
	isSmsRequired,
	registrationInstructions,
}: ParkingDetailsFormProps) => {
	const { isKiosk } = useKiosk();
	const z = useZod();
	const { i18n, t } = useLingui();
	const serverErrorT = (_key: string, options?: { defaultValue?: string }) => options?.defaultValue ?? "";
	const wizardSettings = usePermitWizardSettings();
	const permitFormData = usePermitFormData();
	const { setEmail } = usePermitFormActions();
	const selectedChoice =
		wizardSettings.choices.find((choice) => choice.permitTypeId === permitFormData.parkingChoiceId) ??
		wizardSettings.choices[0];
	const resolvedRegistrationInstructions = useMemo(
		() =>
			resolveRegistrationInstructions({
				baselineInstructions: registrationInstructions,
				wizardSettings,
				formData: permitFormData,
			}),
		[registrationInstructions, wizardSettings, permitFormData],
	);
	const hasCharge = (invoice?.total ?? 0) > 0;
	const hasProrationDiscount = invoice?.discountItems?.some((item) => item.type === "Proration") ?? false;
	const fullTermIntervals = hasProrationDiscount ? selectedChoice?.context?.fixedIntervals : undefined;
	const prorationTimezone = selectedChoice?.time.timezone;
	const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
	const [isSelectVehiclesModalOpen, setIsSelectVehiclesModalOpen] = useState(false);
	const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
	const [selectVehiclesModalInstance, setSelectVehiclesModalInstance] = useState(0);
	const openSelectVehiclesModal = () => {
		setSelectVehiclesModalInstance((instance) => instance + 1);
		setIsSelectVehiclesModalOpen(true);
	};
	const utils = offstreetClient.useUtils();
	const {
		data: parkerVehiclesData,
		error: parkerVehiclesError,
		isPending: isParkerVehiclesPending,
	} = offstreetClient.parkers.getParkerVehicles.useQuery(undefined, {
		refetchOnWindowFocus: false,
		enabled: isLoggedIn,
	});
	const hasSavedVehicles = (parkerVehiclesData?.items.length ?? 0) > 0;
	const isSavedVehiclesLoading = isLoggedIn && isParkerVehiclesPending;
	const showSavedVehiclesSummary = isLoggedIn && !isParkerVehiclesPending && !parkerVehiclesError;
	const showManualVehicleEntry = !isSavedVehiclesLoading && !showSavedVehiclesSummary;
	const defaultVehicleState = getDefaultVehicleState({ permitType });
	const priceBreakdownRef = useRef<HTMLDivElement>(null);
	const skipEmailSuggestion = useRef(false);
	const [validationModalState, setValidationModalState] = useState<{
		suggested: string;
		original: string;
	}>();
	const { initialFormValues } = useRememberedInfo({
		initialValues,
		isEmailRequired: !!isEmailRequired,
		isKiosk,
		isLoggedIn,
		isSmsEnabled,
		isSmsRequired: !!isSmsRequired,
		permitType,
	});

	const methods = useEnhancedForm({
		t: serverErrorT,
		schema: z
			.object({
				vehicles: z
					.array(
						z.object({
							id: z.number().optional(),
							plate: zodPlateValidation,
							state: z.string().min(1, t`State or province is required`),
						}),
					)
					.min(
						1,
						(permitType.maxVehicles ?? 1) === 1
							? t`Please select a vehicle`
							: t`Please select at least 1 vehicle`,
					)
					.max(
						permitType.maxVehicles ?? 1,
						t`${plural(permitType.maxVehicles ?? 1, {
							one: "Please select at most # vehicle",
							other: "Please select at most # vehicles",
						})}`,
					),
				shouldSendEmail: z.boolean(),
				shouldSendSms: z.boolean(),
				email: z.string().optional(),
				phoneNumber: z.string().optional(),
				rememberInfo: z.boolean(),
			})
			.superRefine((data, ctx) => {
				if (data.shouldSendEmail || isEmailRequired) {
					if (!data.email) {
						ctx.addIssue({
							code: "custom",
							path: ["email"],
							message: t`Please enter your email address`,
						});
					} else if (!RFC_5322_EMAIL_REGEX.test(data.email)) {
						ctx.addIssue({
							code: "custom",
							path: ["email"],
							message: t`Please enter a valid email address`,
						});
					}
					return;
				}
			})
			.superRefine((data, ctx) => {
				if (isSmsEnabled && (data.shouldSendSms || isSmsRequired)) {
					if (!data.phoneNumber) {
						ctx.addIssue({
							code: "custom",
							path: ["phoneNumber"],
							message: t`Please enter your phone number`,
						});
					} else if (_isValidPhoneNumber && !_isValidPhoneNumber(data.phoneNumber)) {
						ctx.addIssue({
							code: "custom",
							path: ["phoneNumber"],
							message: t`Enter a valid phone number`,
						});
					}
				}
			})
			.superRefine((data, ctx) => {
				const seen = new Set<string>();
				data.vehicles.forEach((v, i) => {
					const key = `${v.plate}__${v.state}`;
					if (seen.has(key)) {
						ctx.addIssue({
							code: "custom",
							path: ["vehicles", i, "plate"],
							message: t`Please enter unique vehicles.`,
						});
					}
					seen.add(key);
				});
			}),
		initialValues: {
			permitIntervals: initialValues?.permitIntervals ?? [],
			vehicles: initialFormValues.vehicles,
			shouldSendEmail: initialFormValues.shouldSendEmail,
			email: initialFormValues.email,
			shouldSendSms: initialFormValues.shouldSendSms,
			phoneNumber: initialFormValues.phoneNumber,
			locationIds: initialValues?.locationIds ?? [],
			rememberInfo: initialFormValues.rememberInfo,
		},
	});
	const {
		setValue,
		register,
		watch,
		clearErrors,
		trigger,
		formState: { errors },
		control,
		getValues,
	} = methods;
	const {
		fields: vehicles,
		append: addVehicle,
		remove: removeVehicle,
		replace: replaceVehicles,
	} = useFieldArray({
		name: "vehicles",
		control,
	});
	useEffect(() => {
		if (showManualVehicleEntry && vehicles.length === 0) {
			addVehicle({ plate: "", state: defaultVehicleState });
		}
	}, [showManualVehicleEntry, vehicles.length, addVehicle, defaultVehicleState]);
	const accountEmail = permitFormData.email;
	const accountVehicles = permitFormData.vehicles;
	const maxVehicles = permitType.maxVehicles ?? 1;
	useEffect(() => {
		if (accountEmail && !getValues("email")) {
			setValue("email", accountEmail);
			setValue("shouldSendEmail", true);
		}
		if (accountVehicles?.length && !getValues("vehicles")?.some((vehicle) => vehicle.plate)) {
			replaceVehicles(accountVehicles.slice(0, maxVehicles));
		}
	}, [accountEmail, accountVehicles, maxVehicles, getValues, setValue, replaceVehicles]);
	return (
		<>
			<FormProvider {...methods}>
				<form
					id={formId}
					noValidate={true}
					onSubmit={methods.handleSubmit(
						({ email, rememberInfo, ...data }) => {
							if (!isLoggedIn && rememberInfo) {
								const existingInfo = getRememberedInfo();
								setRememberedInfo({
									email,
									phoneNumber: isSmsEnabled ? data.phoneNumber : existingInfo?.phoneNumber,
									vehicles: data.vehicles,
								});
							} else if (!isLoggedIn) {
								clearRememberedInfo();
							}
							const { hasCorrection, suggested } = suggestEmail(email ?? "");
							if (email && hasCorrection && !skipEmailSuggestion.current) {
								setValidationModalState({
									suggested: suggested!,
									original: email,
								});
							} else {
								onSubmit({
									email,
									...data,
									vehicles: data.vehicles.map((vehicle) => ({
										...vehicle,
									})),
								});
							}
						},
						(errors) => {
							const vehicleErrors = errors.vehicles;
							if (vehicleErrors) {
								for (let i = 0; vehicleErrors[i]; i++) {
									if (vehicleErrors[i]?.plate) {
										document
											.querySelector<HTMLInputElement>(`input[name="vehicles.${i}.plate"]`)
											?.focus();
										return;
									}
									if (vehicleErrors[i]?.state) {
										const selects = document.querySelectorAll<HTMLSelectElement>(
											`select[name="vehicles.${i}.state"]`,
										);
										Array.from(selects)
											.find((el) => el.offsetParent !== null)
											?.focus();
										return;
									}
								}
							}
							if (errors.email) {
								document.querySelector<HTMLInputElement>('[name="email"]')?.focus();
								return;
							}
							if (errors.phoneNumber) {
								document.querySelector<HTMLInputElement>('[name="phoneNumber"]')?.focus();
							}
						},
					)}
					className="space-y-6"
				>
					<div className="space-y-8">
						{(hasCharge || selectedChoice?.paymentSchedule) && (
							<div>
								<ParkingCost
									totalCents={invoice?.total}
									currency={currency}
									fullTermIntervals={fullTermIntervals}
									timezone={prorationTimezone}
									paymentSchedule={selectedChoice?.paymentSchedule}
								/>

								{showPriceBreakdown ? (
									<div className="pl-7" ref={priceBreakdownRef} tabIndex={-1}>
										{invoice && hasCharge && (
											<PriceBreakdown
												invoice={invoice}
												country={permitType.company.address?.country ?? undefined}
											/>
										)}
										{selectedChoice?.paymentSchedule && (
											<div className="pt-2">
												<PaymentScheduleOverview
													overview={selectedChoice.paymentSchedule}
													country={permitType.company.address?.country ?? undefined}
												/>
											</div>
										)}
									</div>
								) : (
									<div className="flex justify-start pt-2 pl-6">
										<Button
											intent="tertiary"
											className="font-extrabold text-accent text-xs"
											onClick={() => {
												flushSync(() => setShowPriceBreakdown(true));
												priceBreakdownRef.current?.focus();
											}}
										>
											{selectedChoice?.paymentSchedule
												? t`View payroll breakdown`
												: t`View price breakdown`}
										</Button>
									</div>
								)}
							</div>
						)}

						<div className="max-w-prose space-y-4">
							<div className="space-y-2">
								<div className="flex items-center gap-2">
									<VehicleIcon className="h-6 min-h-6 w-6 min-w-6" aria-hidden="true" />
									<Heading As="h2" className="font-bold leading-6">
										{t`Vehicle Information`}
									</Heading>
								</div>
								{permitType.maxVehicles &&
									permitType.maxVehicles > 1 &&
									// Permits are usually only valid for 1 vehicle at a time, but this is not true today for our print permit customers
									!permitType.company.productFeatures?.printPermit && (
										<Text size="xs" intent="muted">
											<Trans>
												You can add <b>up to {permitType.maxVehicles} vehicles</b> to your
												permit. This permit is valid for one vehicle at a time, unless stated
												otherwise.
											</Trans>
										</Text>
									)}
							</div>

							{isLoggedIn && parkerVehiclesError && (
								<Alert
									type="error"
									title={t`We couldn't load your saved vehicles.`}
									message={t`You can still enter your vehicle details below.`}
								/>
							)}

							{isSavedVehiclesLoading && <Skeleton className="h-[76px] w-full rounded-lg" />}

							{showSavedVehiclesSummary && (
								<SelectedVehiclesSummary
									maxVehicles={permitType.maxVehicles ?? 1}
									onOpenSelectModal={
										hasSavedVehicles
											? openSelectVehiclesModal
											: () => setIsCreateVehicleModalOpen(true)
									}
								/>
							)}
						</div>

						{showManualVehicleEntry && (
							<>
								{vehicles.map((vehicle, i) => (
									<VehicleInput
										key={vehicle.id}
										index={i}
										register={register}
										errors={errors}
										country={permitType.country as CountryValue}
										showRemove={vehicles.length > 1}
										onRemove={() => removeVehicle(i)}
									/>
								))}

								{vehicles.length < (permitType.maxVehicles ?? 1) && (
									<Button
										aria-label={t`Add Vehicle`}
										intent="secondary"
										type="button"
										onClick={() => {
											addVehicle({
												plate: "",
												state: defaultVehicleState,
											});
										}}
									>
										<AddIcon className="h-5 w-5" />
										{t`Add Vehicle`}
									</Button>
								)}
							</>
						)}

						{isSmsEnabled && (
							<div className="max-w-prose space-y-2">
								{!isSmsRequired ? (
									<FormCheckbox
										name="shouldSendSms"
										label={t`Send me a text reminder`}
										labelClassName="font-bold text-base"
										onChange={() => {
											setValue("phoneNumber", "");
										}}
									/>
								) : (
									<Text weight="bold">{t`Send me a text reminder`}</Text>
								)}
								<div className="space-y-2 pl-6">
									<div>
										<Text size="xs">
											{t`Get notified ${formatDuration({ duration: permitType.expiryNotificationOffsetMinutes, i18n })} before your parking expires.`}
										</Text>
										<Text size="xs" intent="muted">
											{t`This is a one-time notification only. We will not send promotional messages or share your number.`}
										</Text>
										<Text size="xs" intent="muted">
											{t`Message & data rates may apply. Reply STOP to opt out.`}
										</Text>
									</div>
									{(isSmsRequired || watch("shouldSendSms")) && (
										<>
											<FormPhoneInput
												name="phoneNumber"
												label={t`Mobile Phone`}
												key={permitType.company.address?.country}
												defaultCountry={permitType.company.address?.country}
												aria-describedby="phone-input-description"
												isRequired
											/>
											<span className="sr-only" id="phone-input-description">
												{t`Enter your mobile phone number.`}
											</span>
										</>
									)}
								</div>
							</div>
						)}

						<div className="max-w-prose space-y-2">
							{!isEmailRequired ? (
								<FormCheckbox
									name="shouldSendEmail"
									data-testid="should-send-email-checkbox"
									label={
										hasCharge
											? t`Email me a receipt and reminders`
											: t`Email me a confirmation and reminders`
									}
									labelClassName="font-bold text-base"
									onChange={() => {
										setValue("email", "");
									}}
								/>
							) : (
								<Text weight="bold">
									{hasCharge
										? t`Email me a receipt and reminders`
										: t`Email me a confirmation and reminders`}
								</Text>
							)}
							{(isEmailRequired || watch("shouldSendEmail")) && (
								<fieldset className="space-y-2 border-none p-0 pl-6" aria-label={t`Email`}>
									<EmailInput
										companyId={permitType.company.id}
										error={errors.email?.message}
										name="email"
										onBlur={() => {
											trigger("email");
											setEmail({ email: getValues("email") });
										}}
										label={t`Email`}
										onChange={(email) => {
											skipEmailSuggestion.current = false;
											clearErrors("email");
											setValue("email", email);
										}}
										placeholder="john_doe@gmail.com"
										defaultValue={watch("email")}
										aria-describedby="email-input-description"
										isRequired
									/>
									<span className="sr-only" id="email-input-description">
										{t`Enter your email address for example: john_doe@gmail.com.`}
									</span>
								</fieldset>
							)}
						</div>

						{!isLoggedIn && !isKiosk && (
							<div className="max-w-prose">
								<FormCheckbox
									name="rememberInfo"
									data-testid="remember-info-checkbox"
									label={t`Remember me on this device`}
									labelClassName="font-bold text-base"
								/>
							</div>
						)}

						{!!resolvedRegistrationInstructions && (
							<div className="xl:hidden">
								<ParkingDetailsRow icon={InfoIcon}>
									<div className={"flex w-full flex-col gap-3"}>
										<Heading As="h2" className="font-bold leading-6">
											{t`Parking instructions`}
										</Heading>
										<WysiwygView content={resolvedRegistrationInstructions} />
									</div>
								</ParkingDetailsRow>
							</div>
						)}

						<EmailValidationModal
							key={validationModalState?.suggested}
							suggested={validationModalState?.suggested}
							original={validationModalState?.original}
							onContinue={(email) => {
								skipEmailSuggestion.current = true;
								setValidationModalState(undefined);
								setValue("email", email);
								(document.getElementById(formId) as HTMLFormElement).requestSubmit();
							}}
							onClose={() => setValidationModalState(undefined)}
						/>
					</div>
				</form>
			</FormProvider>

			{isLoggedIn && (
				<CreateVehicleModal
					isOpen={isCreateVehicleModalOpen}
					shouldDefault={!hasSavedVehicles}
					country={permitType.country as CountryValue}
					defaultState={defaultVehicleState}
					onSuccess={async (vehicle) => {
						const maxVehicles = permitType.maxVehicles ?? 1;
						const currentVehicles = (getValues("vehicles") ?? []).filter(
							(currentVehicle) => currentVehicle.plate,
						);
						const selectedVehicles =
							maxVehicles === 1
								? [vehicle]
								: currentVehicles.length < maxVehicles
									? [...currentVehicles, vehicle]
									: currentVehicles;
						setValue("vehicles", selectedVehicles, {
							shouldDirty: true,
							shouldTouch: true,
						});
						trigger("vehicles");
						await utils.parkers.getParkerVehicles.invalidate();
						setIsCreateVehicleModalOpen(false);
					}}
					onClose={() => setIsCreateVehicleModalOpen(false)}
				/>
			)}

			{isLoggedIn && (
				<SelectVehiclesModal
					key={selectVehiclesModalInstance}
					isOpen={isSelectVehiclesModalOpen}
					maxVehicles={permitType.maxVehicles ?? 1}
					selectedVehicles={watch("vehicles")}
					country={permitType.country as CountryValue}
					defaultVehicleState={defaultVehicleState}
					description={
						(permitType.maxVehicles ?? 1) > 1 && !permitType.company.productFeatures?.printPermit
							? t`This permit is valid for one vehicle at a time, unless stated otherwise.`
							: undefined
					}
					onClose={() => setIsSelectVehiclesModalOpen(false)}
					onConfirm={(selectedVehicles) => {
						setValue("vehicles", selectedVehicles, {
							shouldDirty: true,
							shouldTouch: true,
						});
						trigger("vehicles");
					}}
					onVehicleEdited={(vehicle) => {
						setValue(
							"vehicles",
							(getValues("vehicles") ?? []).map((selectedVehicle) =>
								selectedVehicle.id === vehicle.id ? vehicle : selectedVehicle,
							),
							{
								shouldDirty: true,
								shouldTouch: true,
								shouldValidate: true,
							},
						);
					}}
					onVehicleRemoved={(vehicleId) => {
						setValue(
							"vehicles",
							(getValues("vehicles") ?? []).filter((selectedVehicle) => selectedVehicle.id !== vehicleId),
							{
								shouldDirty: true,
								shouldTouch: true,
								shouldValidate: true,
							},
						);
					}}
				/>
			)}
		</>
	);
};
